import { useEffect, useState } from 'react';
import CampoImporto from '../components/CampoImporto';
import { Link } from 'react-router-dom';
import { commesseApi, clientiApi, offerteApi, listinoApi } from '../api';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import ProgressBar from '../components/ProgressBar';
import PannelloListino from '../components/PannelloListino';

const empty = { offerta_id: '', cliente_id: '', numero: '', titolo: '', descrizione: '', budget: '', data_inizio: '', data_fine_prevista: '', stato: 'attiva', responsabile: '', note: '', tipo_fatturazione: '', periodicita_mesi: '', modalita_esecuzione: 'corpo', tariffa_km: '0.45', tariffa_km_cliente: '' };
const STATI = ['attiva', 'completata', 'sospesa', 'annullata'];
const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

const PERIODI = [
  { v: '', l: 'Non periodica (a SAL / milestone)' },
  { v: '1', l: 'Mensile' },
  { v: '2', l: 'Bimestrale' },
  { v: '3', l: 'Trimestrale' },
  { v: '4', l: 'Quadrimestrale' },
  { v: '6', l: 'Semestrale' },
];

const oggi = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

/** Stato di fatturazione della commessa, usato per l'evidenziazione a colpo d'occhio. */
export function statoFatturazione(r) {
  const budget = parseFloat(r.budget) || 0;
  const daEmettere = parseFloat(r.da_emettere) || 0;
  const fatturato = parseFloat(r.fatturato) || 0;
  // "Completata" richiede che il budget sia stato davvero coperto: una commessa
  // appena creata ha zero da emettere solo perché non ha ancora alcuna fattura.
  if (budget > 0 && daEmettere < 0.01 && fatturato >= budget - 0.01) return 'completata';
  if (r.prossima_fattura) {
    const g = Math.round((new Date(r.prossima_fattura) - oggi()) / 86400000);
    if (g < 0) return 'scaduta';
    if (g <= 15) return 'imminente';
    return 'programmata';
  }
  return 'aperta';
}

const RIGA = {
  completata:   'bg-green-50/70',
  scaduta:      'bg-red-50/70',
  imminente:    'bg-amber-50/70',
  programmata:  '',
  aperta:       '',
};

export default function Commesse() {
  const [rows, setRows] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [filtroStato, setFiltroStato] = useState('attiva');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [ricerca, setRicerca] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [selezione, setSelezione] = useState([]);
  const [offerte, setOfferte] = useState([]);   // offerte richiamabili in creazione
  const [voci, setVoci] = useState([]);         // listino, per le commesse in economia
  const [chiusura, setChiusura] = useState(null);   // proposta di saldo alla chiusura

  // Si scaricano tutte e si filtra qui: i filtri diventano immediati e,
  // soprattutto, si può dire che una commessa esiste ma è fuori dal filtro
  // attivo. Cercare "SA167" con il filtro su "attiva" non dava nulla, e la
  // commessa invece c'era: era completata.
  const load = () => commesseApi.list({}).then(setRows);
  useEffect(() => { load(); }, []);
  useEffect(() => { clientiApi.list().then(setClienti); }, []);

  const openNew = () => {
    setForm(empty); setVoci([]);
    setModal({ mode: 'new' });
    // Richiamabili: le offerte non perse che non hanno ancora generato una commessa
    offerteApi.list({}).then(o =>
      setOfferte(o.filter(x => !x.ha_commessa && !['persa', 'scaduta'].includes(x.stato)))
    ).catch(() => setOfferte([]));
  };
  const openEdit = (r) => {
    setForm({
      ...r,
      data_inizio: r.data_inizio?.slice(0, 10) || '',
      data_fine_prevista: r.data_fine_prevista?.slice(0, 10) || '',
      periodicita_mesi: r.periodicita_mesi ?? '',
      tipo_fatturazione: r.tipo_fatturazione || '',
    });
    setVoci([]);
    setModal({ mode: 'edit', id: r.id });
    if (r.modalita_esecuzione === 'economia') {
      listinoApi.get('commessa', r.id).then(setVoci).catch(() => setVoci([]));
    }
  };

  // Richiama un'offerta: precompila la commessa con i suoi dati e ne eredita il listino
  const richiamaOfferta = async (offertaId) => {
    if (!offertaId) { setForm(p => ({ ...p, offerta_id: '' })); return; }
    const o = offerte.find(x => String(x.id) === String(offertaId));
    if (!o) return;
    setForm(p => ({
      ...p,
      offerta_id: o.id,
      cliente_id: o.cliente_id || '',
      numero: p.numero || o.numero,
      titolo: p.titolo || o.titolo,
      descrizione: p.descrizione || o.descrizione || '',
      budget: o.importo || '',
      responsabile: p.responsabile || o.responsabile || '',
      modalita_esecuzione: o.modalita_esecuzione || 'corpo',
    }));
    if (o.modalita_esecuzione === 'economia') {
      try { setVoci(await listinoApi.get('offerta', o.id)); } catch { setVoci([]); }
    } else setVoci([]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, periodicita_mesi: form.periodicita_mesi === '' ? null : Number(form.periodicita_mesi) };
      const salvata = modal.mode === 'new'
        ? await commesseApi.create(payload)
        : await commesseApi.update(modal.id, payload);

      // Il listino si salva dopo, perché in creazione l'id della commessa nasce ora.
      const idCommessa = modal.mode === 'new' ? salvata?.id : modal.id;
      if (idCommessa) {
        await listinoApi.save('commessa', idCommessa,
          payload.modalita_esecuzione === 'economia' ? voci.filter(v => v.descrizione?.trim()) : []);
      }
      setModal(null); load();
      // Cambiando il riaddebito chilometrico le trasferte già registrate si
      // riallineano da sole: va detto, perché sono importi che qualcuno
      // ritroverà in fattura.
      if (salvata?.riaddebiti_aggiornati > 0) {
        alert(`Riaddebito aggiornato anche su ${salvata.riaddebiti_aggiornati} `
          + `${salvata.riaddebiti_aggiornati === 1 ? 'trasferta già registrata' : 'trasferte già registrate'} `
          + 'nei SAL non ancora approvati. I SAL approvati restano come sono.');
      }
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  // L'eliminazione trascina con sé ciò che è collegato alla commessa. Il
  // server rifiuta se c'è già contabilità (fatture emesse, SAL approvati,
  // costi dei consulenti): quella commessa si annulla, non si elimina. Negli
  // altri casi si dice prima cosa sparirà, e si chiede che fare dell'offerta.
  const del = async (r) => {
    try {
      const x = await commesseApi.riepilogoEliminazione(r.id);
      if (x.blocchi.length) {
        return alert(`La commessa ${x.numero} non si può eliminare: ha ${x.blocchi.join(', ')}.\n\n`
          + 'Eliminandola si perderebbero questi dati. Mettila in stato "annullata" (Modifica) invece di eliminarla.');
      }
      const msg = `Eliminare la commessa ${x.numero} — ${x.titolo}?`
        + (x.cancellati.length ? `\n\nVerranno eliminati anche: ${x.cancellati.join(', ')}.` : '');
      if (!confirm(msg)) return;
      // L'offerta di origine: senza commessa, "vinta" direbbe il falso.
      const riporta = x.offerta_id && x.offerta_stato === 'vinta' && confirm(
        `La commessa nasce dall'offerta ${x.offerta_numero}, oggi "vinta".\n\n`
        + 'Riportare l\'offerta allo stato "inviata"?\n(OK = sì, Annulla = resta "vinta")');
      await commesseApi.delete(r.id, riporta ? { offerta: 'inviata' } : undefined);
      setSelezione(s => s.filter(y => y !== r.id));
      load();
    } catch (e) { alert(e); }
  };

  const eliminaSelezionate = async () => {
    const scelte = rows.filter(r => selezione.includes(r.id));
    try {
      const riepiloghi = await Promise.all(scelte.map(r => commesseApi.riepilogoEliminazione(r.id)));
      const bloccate = riepiloghi.filter(x => x.blocchi.length);
      const eliminabili = riepiloghi.filter(x => !x.blocchi.length);
      if (!eliminabili.length) {
        return alert('Nessuna delle commesse scelte si può eliminare: hanno tutte fatture emesse, SAL approvati '
          + 'o costi dei consulenti. Mettile in stato "annullata" invece di eliminarle.');
      }
      const msg = `Eliminare definitivamente ${eliminabili.length} commesse?`
        + (bloccate.length ? `\n\nNe resteranno ${bloccate.length} (${bloccate.map(x => x.numero).join(', ')}): `
            + 'hanno fatture emesse, SAL approvati o costi dei consulenti, quindi vanno annullate, non eliminate.' : '')
        + '\n\nVerranno eliminati anche SAL non approvati, fatture da emettere, offerte dei consulenti e allegati collegati.';
      if (!confirm(msg)) return;
      const conOfferta = eliminabili.filter(x => x.offerta_id && x.offerta_stato === 'vinta');
      const riporta = conOfferta.length > 0 && confirm(
        `${conOfferta.length} di queste commesse nascono da offerte oggi "vinte" `
        + `(${conOfferta.map(x => x.offerta_numero).join(', ')}).\n\n`
        + 'Riportare quelle offerte allo stato "inviata"?\n(OK = sì, Annulla = restano "vinte")');
      for (const x of eliminabili) await commesseApi.delete(x.id, riporta ? { offerta: 'inviata' } : undefined);
      setSelezione([]); load();
    } catch (e) { alert(e); load(); }
  };

  // Prima di chiudere si guarda se resta qualcosa da fatturare: chiudere una
  // commessa con un residuo scoperto significa perdere quei soldi.
  const chiudi = async (r) => {
    try {
      const rp = await commesseApi.riepilogoChiusura(r.id);
      if (rp.proponi_fattura) {
        setChiusura({ ...rp, importo: String(rp.residuo), crea: true,
                      descrizione: `Saldo a chiusura — ${rp.titolo || ''}`.trim() });
        return;
      }
      await commesseApi.chiudi(r.id);
      setSelezione(s => s.filter(x => x !== r.id));
      load();
    } catch (e) { alert(e); }
  };

  const confermaChiusura = async () => {
    try {
      await commesseApi.chiudi(chiusura.id, chiusura.crea ? {
        fattura_residuo: true,
        importo_residuo: Number(chiusura.importo),
        descrizione_residuo: chiusura.descrizione,
      } : {});
      setChiusura(null);
      setSelezione(s => s.filter(x => x !== chiusura.id));
      load();
    } catch (e) { alert(e); }
  };
  // Il pulsante occupa la stessa posizione di "Chiudi" e si trasforma sotto il
  // cursore appena la commessa è chiusa: senza conferma un secondo clic la
  // riapriva in silenzio.
  const riapri = async (r) => {
    if (!confirm(`Riaprire la commessa ${r.numero} — ${r.titolo}?\n\nTornerà fra le attive e la data di chiusura verrà azzerata.`)) return;
    try { await commesseApi.riapri(r.id); load(); } catch (e) { alert(e); }
  };

  const chiudiSelezionate = async () => {
    if (!selezione.length) return;
    if (!confirm(`Chiudere ${selezione.length} commesse? Verranno marcate come completate con data di oggi.`)) return;
    try {
      const r = await commesseApi.chiudiMultiple(selezione);
      setSelezione([]);
      load();
      alert(`${r.chiuse} commesse chiuse.`);
    } catch (e) { alert(e); }
  };

  const f = (k) => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  const parole = ricerca.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const passaTesto = (r) => !parole.length || parole.every(w =>
    `${r.numero || ''} ${r.titolo || ''} ${r.cliente_nome || ''} ${r.stato || ''} ${r.responsabile || ''}`
      .toLowerCase().includes(w));
  const passaStato = (r) => !filtroStato || r.stato === filtroStato;
  const passaCliente = (r) => !filtroCliente || String(r.cliente_id) === String(filtroCliente);

  const visibili = rows.filter(r => passaTesto(r) && passaStato(r) && passaCliente(r));
  // Quando la ricerca non trova niente qui ma qualcosa c'è altrove, lo si dice:
  // è l'unica informazione utile in quel momento.
  const altrove = parole.length ? rows.filter(r => passaTesto(r) && !(passaStato(r) && passaCliente(r))) : [];

  const nCompletate = visibili.filter(r => statoFatturazione(r) === 'completata').length;
  const nScadute = visibili.filter(r => statoFatturazione(r) === 'scaduta').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Commesse</h1>
        <button className="btn-primary" onClick={openNew}>+ Nuova commessa</button>
      </div>

      {/* Filtri */}
      <div className="flex gap-2 flex-wrap items-center">
        {['', ...STATI].map(s => (
          <button key={s} onClick={() => setFiltroStato(s)}
            className={`btn-sm btn ${filtroStato === s ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
            {s || 'Tutte'}
          </button>
        ))}
        <select className="input py-1 text-sm max-w-[240px]" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Tutti i clienti</option>
          {clienti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale || c.nome}</option>)}
        </select>
        <input className="input py-1 text-sm max-w-[220px]" placeholder="Cerca numero, titolo, cliente…"
               value={ricerca} onChange={e => setRicerca(e.target.value)} />
        {ricerca && (
          <span className="text-xs text-gray-500">
            {visibili.length} {visibili.length === 1 ? 'commessa' : 'commesse'}
          </span>
        )}
      </div>

      {altrove.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
          {visibili.length === 0
            ? <>Nessuna commessa qui, ma <strong>{altrove.length}</strong> {altrove.length === 1 ? 'corrisponde' : 'corrispondono'} alla ricerca fuori dal filtro attivo</>
            : <>Altre <strong>{altrove.length}</strong> corrispondono alla ricerca ma sono fuori dal filtro attivo</>}
          {' '}({[...new Set(altrove.map(r => r.stato))].join(', ')}).
          <button className="ml-1.5 text-brand-700 font-medium hover:underline"
                  onClick={() => { setFiltroStato(''); setFiltroCliente(''); }}>
            mostrale tutte
          </button>
        </div>
      )}

      {/* Azioni su selezione multipla — utile dopo l'import dello storico */}
      {selezione.length > 0 && (
        <div className="flex items-center gap-3 bg-brand-50 border border-brand-200 rounded-lg px-4 py-2">
          <span className="text-sm text-brand-800 font-medium">{selezione.length} selezionate</span>
          <button className="btn-sm btn bg-green-600 text-white hover:bg-green-700 font-semibold" onClick={chiudiSelezionate}>
            ✓ Chiudi selezionate
          </button>
          <button className="btn-sm btn bg-red-600 text-white hover:bg-red-700 font-semibold" onClick={eliminaSelezionate}>
            🗑 Elimina selezionate
          </button>
          <button className="btn-sm btn bg-white border border-gray-300 text-gray-600" onClick={() => setSelezione([])}>
            Annulla selezione
          </button>
        </div>
      )}

      {/* Legenda colori */}
      <div className="flex gap-4 flex-wrap text-xs text-gray-500 items-center">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-100 border border-green-300" /> Interamente fatturata ({nCompletate})</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Fattura periodica scaduta ({nScadute})</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> In scadenza entro 15 giorni</span>
      </div>

      {/* overflow-x-auto, non hidden: su schermi stretti le ultime colonne
          (con i pulsanti) venivano tagliate via senza possibilità di scorrere */}
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  title="Seleziona tutte le righe visibili"
                  checked={visibili.length > 0 && selezione.length === visibili.length}
                  onChange={e => setSelezione(e.target.checked ? visibili.map(r => r.id) : [])}
                />
              </th>
              <th className="px-4 py-3">Numero</th>
              <th className="px-4 py-3">Titolo</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3 text-right">Budget</th>
              <th className="px-4 py-3 text-right">Fatturato</th>
              <th className="px-4 py-3 text-right">Da emettere</th>
              <th className="px-4 py-3 w-40">Avanzamento</th>
              <th className="px-4 py-3">Prossima fattura</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visibili.length === 0 && <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-400">Nessuna commessa</td></tr>}
            {visibili.map(r => {
              const st = statoFatturazione(r);
              const daEm = parseFloat(r.da_emettere) || 0;
              const giorni = r.prossima_fattura
                ? Math.round((new Date(r.prossima_fattura) - oggi()) / 86400000) : null;
              return (
                <tr key={r.id} className={`border-b last:border-0 hover:bg-gray-100 ${RIGA[st]}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={selezione.includes(r.id)}
                      onChange={e => setSelezione(s => e.target.checked ? [...s, r.id] : s.filter(x => x !== r.id))}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-gray-600">
                    <Link to={`/commesse/${r.id}`} className="hover:text-brand-600 hover:underline"
                          title="Apri il dettaglio">{r.numero}</Link>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <Link to={`/commesse/${r.id}`} className="hover:text-brand-600 hover:underline">{r.titolo}</Link>
                    {r.tipo_fatturazione && <div className="text-xs text-gray-400 font-normal truncate max-w-[220px]">{r.tipo_fatturazione}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.cliente_nome}</td>
                  <td className="px-4 py-3 font-medium text-right">{fmt(r.budget)}</td>
                  <td className="px-4 py-3 text-right">{fmt(r.fatturato)}</td>
                  <td className={`px-4 py-3 text-right ${daEm > 0 ? 'text-yellow-700 font-medium' : 'text-gray-300'}`}>
                    {daEm > 0 ? fmt(daEm) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {st === 'completata'
                      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">✓ Fatturata al 100%</span>
                      : <ProgressBar value={r.fatturato} max={r.budget} />}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.prossima_fattura ? (
                      <span className={st === 'scaduta' ? 'text-red-600 font-semibold' : st === 'imminente' ? 'text-amber-700 font-medium' : 'text-gray-500'}>
                        {fmtDate(r.prossima_fattura)}
                        <div className="text-[11px] font-normal">
                          {giorni < 0 ? `in ritardo di ${Math.abs(giorni)} gg` : giorni === 0 ? 'oggi' : `fra ${giorni} gg`}
                        </div>
                      </span>
                    ) : daEm > 0 ? <span className="text-gray-400">a SAL / milestone</span> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge stato={r.stato} /></td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1 justify-end whitespace-nowrap">
                      {r.stato === 'attiva' ? (
                        <button
                          className="btn-sm btn bg-green-100 text-green-700 hover:bg-green-200 font-semibold"
                          title="Chiudi la commessa (completata, con data di oggi)"
                          onClick={() => chiudi(r)}
                        >✓ Chiudi</button>
                      ) : r.stato === 'completata' ? (
                        <button
                          className="btn-sm btn bg-gray-100 text-gray-600 hover:bg-gray-200"
                          title="Riporta la commessa fra le attive"
                          onClick={() => riapri(r)}
                        >↺ Riapri</button>
                      ) : null}
                      {/* Il dettaglio si apre dal titolo: qui bastano matita e cestino */}
                      <button className="btn-secondary btn-sm px-2" title="Modifica la commessa"
                              onClick={() => openEdit(r)}>✎</button>
                      <button className="btn-danger btn-sm px-2" title="Elimina la commessa"
                              onClick={() => del(r)}>🗑</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {chiusura && (
        <Modal title={`Chiusura commessa ${chiusura.numero}`} onClose={() => setChiusura(null)} size="lg">
          <div className="space-y-4">
            <div className="text-sm text-gray-700">{chiusura.titolo}</div>

            <div className="grid grid-cols-3 gap-3">
              {[['Budget', chiusura.budget, 'text-gray-900'],
                ['Già fatturato', chiusura.fatturato, 'text-brand-600'],
                ['Già in coda da emettere', chiusura.gia_da_emettere, 'text-amber-600']].map(([l, v, c]) => (
                <div key={l} className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">{l}</div>
                  <div className={`text-lg font-bold mt-0.5 ${c}`}>{fmt(v)}</div>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-3">
              <div className="text-sm text-gray-800">
                Resterebbero <strong>{fmt(chiusura.residuo)}</strong> non fatturati.
                Vuoi metterli fra le fatture da emettere prima di chiudere?
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" className="w-4 h-4" checked={chiusura.crea}
                       onChange={e => setChiusura(c => ({ ...c, crea: e.target.checked }))} />
                Crea la fattura di saldo
              </label>

              {chiusura.crea && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="label text-xs">Descrizione</label>
                    <input className="input py-1 text-sm" value={chiusura.descrizione}
                           onChange={e => setChiusura(c => ({ ...c, descrizione: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label text-xs">Importo (€)</label>
                    <input className="input py-1 text-sm text-right" type="number" step="0.01"
                           value={chiusura.importo}
                           onChange={e => setChiusura(c => ({ ...c, importo: e.target.value }))} />
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500">
                Il residuo tiene già conto degli avanzamenti approvati e di quanto è
                in coda: non rischi di fatturare due volte lo stesso importo.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setChiusura(null)}>Annulla</button>
            <button className="btn-primary" onClick={confermaChiusura}>
              {chiusura.crea ? '✓ Chiudi e crea fattura' : '✓ Chiudi senza fatturare'}
            </button>
          </div>
        </Modal>
      )}

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Nuova commessa' : 'Modifica commessa'} onClose={() => setModal(null)}
               size={form.modalita_esecuzione === 'economia' ? 'xl' : 'lg'}>
          <div className="grid grid-cols-2 gap-4">
            {modal.mode === 'new' && (
              <div className="col-span-2">
                <label className="label">
                  Da offerta
                  <span className="ml-1 text-xs text-gray-400 font-normal">(facoltativa: richiama un'offerta e precompila la commessa)</span>
                </label>
                <select className="input" value={form.offerta_id || ''} onChange={e => richiamaOfferta(e.target.value)}>
                  <option value="">— Nessuna: commessa libera —</option>
                  {offerte.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.numero} — {o.titolo} {o.cliente_nome ? `(${o.cliente_nome})` : ''} · {fmt(o.importo)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="label">Cliente</label>
              <select className="input" value={form.cliente_id || ''} onChange={e => setForm(p => ({ ...p, cliente_id: e.target.value }))}>
                <option value="">— Seleziona —</option>
                {clienti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale || c.nome}</option>)}
              </select>
            </div>
            <div><label className="label">Numero commessa *</label><input className="input" {...f('numero')} /></div>
            <div className="col-span-2"><label className="label">Titolo *</label><input className="input" {...f('titolo')} /></div>
            <div><label className="label">Budget (€)</label><CampoImporto className="input" value={form.budget} onChange={v => setForm(p => ({ ...p, budget: v }))} /></div>
            <div>
              <label className="label">Stato</label>
              <select className="input" value={form.stato} onChange={e => setForm(p => ({ ...p, stato: e.target.value }))}>
                {STATI.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="label">
                Modalità di esecuzione
                <span className="ml-1 text-xs text-gray-400 font-normal">(determina cosa rendicontano i consulenti)</span>
              </label>
              <select className="input" value={form.modalita_esecuzione || 'corpo'}
                      onChange={e => setForm(p => ({ ...p, modalita_esecuzione: e.target.value }))}>
                <option value="corpo">A corpo — avanzamento in percentuale sull'offerta</option>
                <option value="economia">In economia — ore/giornate a consuntivo e spese</option>
              </select>
            </div>

            {form.modalita_esecuzione === 'economia' && (
              <div className="col-span-2 space-y-3">
                <PannelloListino voci={voci} onChange={setVoci} />
              </div>
            )}

            {/* I chilometri sono due numeri diversi, e confonderli è costato
                fatture sbagliate: quello che rimborsiamo a chi guida e quello
                che riaddebitiamo al cliente, che è scritto nel contratto. */}
            <div className="col-span-2 grid grid-cols-2 gap-3 bg-gray-50 border rounded-lg p-3">
              <div>
                <label className="label text-xs">Rimborso €/km a chi viaggia</label>
                <input className="input py-1 text-sm" type="number" step="0.001" {...f('tariffa_km')} />
                <p className="text-xs text-gray-400 mt-0.5">Il nostro costo: va al consulente o al dipendente.</p>
              </div>
              <div>
                <label className="label text-xs">Riaddebito €/km al cliente</label>
                <input className="input py-1 text-sm" type="number" step="0.001" placeholder="0,65"
                       value={form.tariffa_km_cliente ?? ''}
                       onChange={e => setForm(p => ({ ...p, tariffa_km_cliente: e.target.value }))} />
                <p className="text-xs text-gray-400 mt-0.5">
                  Quanto previsto dal contratto. Vuoto = 0,65 €/km, la clausola delle nostre
                  offerte; 0 = spese comprese nel corrispettivo.
                </p>
              </div>
            </div>

            <div className="col-span-2"><label className="label">Modalità di fatturazione</label><input className="input" placeholder="es. 50-50%, Fatturazione bimestrale…" {...f('tipo_fatturazione')} /></div>
            <div className="col-span-2">
              <label className="label">
                Periodicità
                <span className="ml-1 text-xs text-gray-400 font-normal">(se impostata, il sistema calcola quando emettere la prossima fattura)</span>
              </label>
              <select className="input" value={form.periodicita_mesi ?? ''} onChange={e => setForm(p => ({ ...p, periodicita_mesi: e.target.value }))}>
                {PERIODI.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </div>
            <div><label className="label">Data inizio</label><input className="input" type="date" {...f('data_inizio')} /></div>
            <div><label className="label">Data fine prevista</label><input className="input" type="date" {...f('data_fine_prevista')} /></div>
            <div className="col-span-2"><label className="label">Responsabile</label><input className="input" {...f('responsabile')} /></div>
            <div className="col-span-2"><label className="label">Descrizione</label><textarea className="input" rows={2} {...f('descrizione')} /></div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={2} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
